import { RESOLVED_BACKEND_URL, cryptoSignalApi } from './api';
import { isDevHost } from '@/lib/appConfig';
import {
  DefaultPageConfig,
  normalizePageConfig,
  type Branding,
  type ContactList,
  type FeatureFlags,
  type SubscriptionListConfig,
  type FirebaseWebConfig,
  type PageConfig,
  type SocialLinks,
} from '@/models/PageConfig';
import { Storage } from './storage';
import { PAGE_ID, PAGE_TITLE } from '@config';
import { decryptJsonPayload, isSecurePayloadEnvelope } from './securePayloadCipher';
import { parseFirebaseConfig } from '@/lib/parseFirebaseConfig';

const CACHE_KEY = `page:config:${PAGE_ID}:v3`;

function safeJson(value: unknown): unknown {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value.trim());
  } catch {
    return null;
  }
}

function firstItem(value: unknown): Record<string, unknown> | null {
  if (Array.isArray(value)) {
    return value.find((v) => v && typeof v === 'object') as Record<string, unknown> | null;
  }
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  return null;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const t = value.trim().toLowerCase();
    if (t === 'true' || t === '1') return true;
    if (t === 'false' || t === '0') return false;
  }
  return fallback;
}

function extractFirebaseWebFromList(firebaseList: unknown): FirebaseWebConfig | undefined {
  const parsed = safeJson(firebaseList);
  if (parsed == null) return undefined;
  const items = Array.isArray(parsed) ? parsed : [parsed];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const fc = (item as { FirebaseConfig?: unknown }).FirebaseConfig;
    const config = parseFirebaseConfig(fc);
    if (!config) continue;
    const apiKey = str(config.apiKey);
    const projectId = str(config.projectId);
    if (!apiKey || !projectId) continue;
    return {
      ApiKey: apiKey,
      AuthDomain: str(config.authDomain),
      ProjectId: projectId,
      StorageBucket: str(config.storageBucket),
      MessagingSenderId: str(config.messagingSenderId),
      AppId: str(config.appId),
      MeasurementId: str(config.measurementId) || undefined,
      VapidKey: str(config.vapidKey) || undefined,
    };
  }
  return undefined;
}

function mapPagesRowToConfig(row: Record<string, unknown>): PageConfig {
  const contactObj = (firstItem(safeJson(row.ContactList)) ?? {}) as Record<string, unknown>;
  const facebookObj = (firstItem(safeJson(row.FacebookList)) ?? {}) as Record<string, unknown>;
  const featureObj = (firstItem(safeJson(row.FeatureList)) ?? {}) as Record<string, unknown>;

  const branding: Branding = {
    Title: str(row.Title) || PAGE_TITLE,
    LogoUrl: str(row.Thumbnail) || undefined,
  };

  const contact: ContactList = {
    Email: str(contactObj.Email ?? contactObj.ContactEmail) || undefined,
    Phone: str(contactObj.PhoneOne) || undefined,
    Address: str(contactObj.Address) || undefined,
    AndroidUrl: str(contactObj.AndroidUrl ?? contactObj.AndroidURL) || undefined,
    IosUrl: str(contactObj.IosUrl ?? contactObj.AppleIosURL) || undefined,
    PrivacyHtml: str(row.PrivacyPolicy) || undefined,
    ContactHtml: str(row.Contact ?? row.Description) || undefined,
  };

  const social: SocialLinks = {
    Facebook: str(facebookObj.FacebookURL) || undefined,
    Telegram: str(contactObj.Telegram) || undefined,
  };

  const web = extractFirebaseWebFromList(row.FirebaseList);
  const featureFlags: FeatureFlags = {
    ContactDialogOnStart: bool(featureObj.ContactDialogOnStart, !!(contact.AndroidUrl || contact.IosUrl)),
  };

  const subscriptionObj = (firstItem(safeJson(row.SubscriptionList)) ?? {}) as Record<
    string,
    unknown
  >;
  const lockedRaw = subscriptionObj.LockedFeatures ?? subscriptionObj.lockedFeatures;
  const lockedFeatures = Array.isArray(lockedRaw)
    ? lockedRaw.map(String)
    : typeof lockedRaw === 'string'
      ? (safeJson(lockedRaw) as string[] | null) ?? []
      : [];

  const subscriptionList: SubscriptionListConfig = {
    CheckoutCurrency: str(subscriptionObj.CheckoutCurrency) || 'USD',
    DailyUsdRate: num(subscriptionObj.DailyUsdRate, 0),
    LockedFeatures: lockedFeatures,
    MonthlyKPayAmount: str(subscriptionObj.MonthlyKPayAmount) || undefined,
  };

  return normalizePageConfig({
    PageId: num(row.Id, PAGE_ID),
    Title: str(row.Title) || PAGE_TITLE,
    LatestAppVersion: num(row.PageAppVersion ?? row.AppVersion, DefaultPageConfig.LatestAppVersion),
    ThumbnailUrl: str(row.Thumbnail) || undefined,
    Branding: branding,
    ContactList: contact,
    SocialLinks: social,
    FeatureList: featureFlags,
    SubscriptionList: subscriptionList,
    FirebaseList: { Web: web },
  });
}

function mergeWebFirebase(server: FirebaseWebConfig | undefined): FirebaseWebConfig | undefined {
  const env = import.meta.env;
  const pick = (a?: string, b?: string) => (a && a.length > 0 ? a : (b ?? '')) || '';
  const merged: FirebaseWebConfig = {
    ApiKey: pick(server?.ApiKey, env.VITE_FIREBASE_API_KEY),
    AuthDomain: pick(server?.AuthDomain, env.VITE_FIREBASE_AUTH_DOMAIN),
    ProjectId: pick(server?.ProjectId, env.VITE_FIREBASE_PROJECT_ID),
    StorageBucket: pick(server?.StorageBucket, env.VITE_FIREBASE_STORAGE_BUCKET),
    MessagingSenderId: pick(server?.MessagingSenderId, env.VITE_FIREBASE_MESSAGING_SENDER_ID),
    AppId: pick(server?.AppId, env.VITE_FIREBASE_APP_ID),
    VapidKey: pick(server?.VapidKey, env.VITE_FIREBASE_VAPID_KEY) || undefined,
  };
  if (!merged.ApiKey || !merged.ProjectId) return server;
  return merged;
}

async function fetchPageRow(signal?: AbortSignal): Promise<PageConfig> {
  const url = `${RESOLVED_BACKEND_URL}${cryptoSignalApi('/page/config')}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageId: PAGE_ID }),
    signal,
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(`Page config failed (${res.status})`);
  }
  let row: Record<string, unknown>;
  if (isSecurePayloadEnvelope(body)) {
    row = (await decryptJsonPayload<Record<string, unknown>>(body)) ?? {};
  } else if (body && typeof body === 'object') {
    row = body as Record<string, unknown>;
  } else {
    throw new Error('Invalid page config response');
  }
  const config = mapPagesRowToConfig(row);
  const web = mergeWebFirebase(config.FirebaseList.Web);
  if (web) config.FirebaseList.Web = web;
  return config;
}

export function compareAppVersion(serverVersion: number, localVersion: number): number {
  if (serverVersion > localVersion) return 1;
  if (serverVersion < localVersion) return -1;
  return 0;
}

export async function getCachedPageConfig(): Promise<PageConfig | null> {
  const cached = await Storage.getJson<PageConfig>(CACHE_KEY);
  if (!cached) return null;
  return normalizePageConfig(cached);
}

export async function loadPageConfig(signal?: AbortSignal): Promise<PageConfig> {
  const config = await fetchPageRow(signal);
  await Storage.setJson(CACHE_KEY, config);
  if (isDevHost()) {
    // eslint-disable-next-line no-console
    console.log('[pageApi] loaded', config);
  }
  return config;
}

export async function refreshPageConfig(signal?: AbortSignal): Promise<PageConfig | null> {
  try {
    return await loadPageConfig(signal);
  } catch {
    return null;
  }
}
